"use client"

import { useRef, useCallback, type KeyboardEvent, type ChangeEvent } from "react"
import { SendHorizontal } from "lucide-react"

interface ChatInputProps {
  onSend: (content: string) => void
  disabled?: boolean
}

const MAX_CHARS = 4000

export function ChatInput({ onSend, disabled }: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const resetHeight = useCallback(() => {
    const el = textareaRef.current
    if (el) {
      el.style.height = "auto"
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`
    }
  }, [])

  const handleSend = useCallback(() => {
    const el = textareaRef.current
    if (!el || disabled) return
    const value = el.value.trim()
    if (!value) return
    onSend(value)
    el.value = ""
    el.style.height = "auto"
  }, [onSend, disabled])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend]
  )

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      if (e.target.value.length > MAX_CHARS) {
        e.target.value = e.target.value.slice(0, MAX_CHARS)
      }
      resetHeight()
    },
    [resetHeight]
  )

  return (
    <div className="lcw-foot">
      <div className="lcw-form">
        <label htmlFor="lawn-care-chat-input" className="sr-only">
          Ask the lawn care expert
        </label>
        <textarea
          name="chat-message"
          ref={textareaRef}
          id="lawn-care-chat-input"
          rows={1}
          maxLength={MAX_CHARS}
          placeholder="Ask a question..."
          className="lcw-input"
          style={{ maxHeight: 120 }}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          disabled={disabled}
          data-chat-input
          aria-describedby="lawn-care-chat-input-help"
        />
        <span id="lawn-care-chat-input-help" className="sr-only">
          Press Enter to send. Press Shift and Enter for a new line.
        </span>
        <button
          className="lcw-send"
          onClick={handleSend}
          disabled={disabled}
          aria-label="Send message"
        >
          <SendHorizontal size={20} aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
